import React, { useEffect, useRef, useState, useMemo } from "react";
import { Modal } from "react-bootstrap";
import { FiSearch, FiChevronLeft, FiChevronRight, FiChevronsLeft, FiChevronsRight } from "react-icons/fi";
import defaultAvatar from "../../../../../assets/images/sem-imagem-small.jpg";

import { getMenuData, loginSecondary } from "../../../../../services/NexiDigitalAPI";
import { getUsersSecondary } from "../../../../../services/NexiDigitalAPISecondary";

const SelectUserModal = ({ show = false, idCompany, onClose = () => {}, onSelect = () => {} }) => {
  const [query, setQuery] = useState("");
  const [appliedQuery, setAppliedQuery] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [pageRows, setPageRows] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [secReady, setSecReady] = useState(false);
  const runIdRef = useRef(0);

  const effectiveCompanyId = useMemo(() => {
    if (idCompany && idCompany !== "all") return idCompany;
    const menu = getMenuData?.() || {};
    return menu.idCompany || undefined;
  }, [idCompany]);

  const commitSearch = () => {
    setAppliedQuery(query.trim());
    setPage(1);
  };

  useEffect(() => {
    if (!show) return;

    setQuery("");
    setAppliedQuery("");
    setPage(1);
    setSecReady(false);
    localStorage.removeItem("token-secondary");

    async function renewSecondaryToken() {
      if (!effectiveCompanyId) {
        setSecReady(true);
        return;
      }

      try {
        setLoading(true);

        const menu = getMenuData() || {};

        const loginSecondaryData = {
          idCompany: Number(effectiveCompanyId),
          idApp: menu.idApp,
          idChannel: menu.idChannel,
          idAppType: menu.idAppType,
        };

        const { data } = await loginSecondary(loginSecondaryData);
        const secondary = data?.newToken || data?.token;

        if (secondary) {
          localStorage.setItem("token-secondary", secondary);
        }
      } catch (err) {
        console.error("Erro ao renovar token secundário", err);
      } finally {
        setSecReady(true);
        setLoading(false);
      }
    }

    renewSecondaryToken();
  }, [show, effectiveCompanyId]);

  useEffect(() => {
    if (!show || !secReady) return;

    const runId = ++runIdRef.current;
    setLoading(true);

    const limit = Number(pageSize) || 10;
    const currentPage = Number(page) || 1;
    const offset = (currentPage - 1) * limit;

    getUsersSecondary({
      offset,
      limit,
      searchString: appliedQuery || "",
    })
      .then((res) => {
        if (runId !== runIdRef.current) return;

        const data = res?.data ?? {};
        const list = Array.isArray(data?.userList) ? data.userList : [];
        const total = Number(data?.totalRecords) || 0;

        setPageRows(list);
        setTotalRecords(total);

        const totalPages = Math.max(1, Math.ceil(total / limit));
        if (currentPage > totalPages && total > 0) setPage(totalPages);
      })
      .catch(() => {
        if (runId !== runIdRef.current) return;
        setPageRows([]);
        setTotalRecords(0);
      })
      .finally(() => {
        if (runId === runIdRef.current) setLoading(false);
      });
  }, [show, secReady, page, pageSize, appliedQuery]);

  const total = Number(totalRecords) || 0;
  const currentPage = Number(page) || 1;
  const limitNum = Number(pageSize) || 10;
  const totalPages = Math.max(1, Math.ceil(total / limitNum));
  const canPrev = currentPage > 1;
  const canNext = currentPage < totalPages;

  const startIndex = total === 0 ? 0 : (currentPage - 1) * limitNum;
  const endIndex = total === 0 ? 0 : Math.min(startIndex + pageRows.length, total);

  const goFirst = () => canPrev && setPage(1);
  const goPrev = () => canPrev && setPage((p) => Math.max(1, p - 1));
  const goNext = () => canNext && setPage((p) => Math.min(totalPages, p + 1));
  const goLast = () => canNext && setPage(totalPages);

  const Cell = ({ children, style }) => (
    <div
      style={{
        padding: "14px 16px",
        borderBottom: "1px solid #eee",
        display: "flex",
        alignItems: "center",
        minHeight: 52,
        height: "100%",
        boxSizing: "border-box",
        ...style,
      }}
    >
      {children}
    </div>
  );

  const HeaderCell = ({ children, style }) => (
    <div
      style={{
        padding: "12px 16px",
        color: "#fff",
        fontWeight: 600,
        ...style,
      }}
    >
      {children}
    </div>
  );

  return (
    <Modal show={show} onHide={onClose} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>Selecionar aluno</Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ paddingTop: 10, paddingBottom: 0, paddingLeft: 20, paddingRight: 24 }}>
        <div style={{ position: "relative", marginBottom: 16 }}>
          <input
            placeholder="Pesquisar…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                if (!loading) commitSearch();
              }
            }}
            style={{
              width: "100%",
              height: 40,
              border: "1px solid #E3E3E3",
              borderRadius: 6,
              outline: "none",
              padding: "0 40px 0 14px",
              fontSize: 14,
            }}
            disabled={loading}
          />
          <FiSearch
            size={18}
            style={{
              position: "absolute",
              right: 12,
              top: "50%",
              transform: "translateY(-50%)",
              opacity: 0.8,
              cursor: loading ? "not-allowed" : "pointer",
            }}
            onClick={() => !loading && commitSearch()}
            title="Buscar"
          />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "2fr 2fr 1fr 160px", background: "#6b48ff", borderRadius: 6 }}>
          <HeaderCell>Nome</HeaderCell>
          <HeaderCell>E-mail</HeaderCell>
          <HeaderCell>Cargo</HeaderCell>
          <HeaderCell />
        </div>

        <div>
          {loading ? (
            <div
              style={{
                padding: "48px 16px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 12,
                borderBottom: "1px solid #eee",
              }}
            >
              <div className="spinner-border" role="status" aria-hidden="true" />
              <span style={{ color: "#777" }}>Carregando usuários...</span>
            </div>
          ) : pageRows.length > 0 ? (
            pageRows.map((u, idx) => (
              <div
                key={`${u?.id || u?.idUser || u?.userId || u?.email || idx}`}
                style={{ display: "grid", gridTemplateColumns: "2fr 2fr 1fr 160px", alignItems: "stretch" }}
              >
                <Cell>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ width: 24, height: 24, borderRadius: "50%", background: "#eee", overflow: "hidden", flexShrink: 0 }}>
                      <img
                        src={u?.photo || u?.avatar || defaultAvatar}
                        alt=""
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = defaultAvatar;
                        }}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    </div>
                    <span>{u?.name || "-"}</span>
                  </div>
                </Cell>

                <Cell>
                  <span style={{ wordBreak: "break-word" }}>{u?.email || "-"}</span>
                </Cell>

                <Cell>{u?.role || "-"}</Cell>

                <Cell style={{ justifyContent: "flex-end" }}>
                  <button type="button" className="btn btn-warning" onClick={() => onSelect(u)} style={{ minWidth: 120 }}>
                    Selecionar
                  </button>
                </Cell>
              </div>
            ))
          ) : (
            <div style={{ padding: "24px 16px", textAlign: "center", color: "#777", borderBottom: "1px solid #eee" }}>
              Nenhum usuário encontrado
            </div>
          )}
        </div>
      </Modal.Body>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 16, padding: "12px 16px", borderTop: "1px solid #eee" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", justifyContent: "flex-end" }}>
          <span style={{ fontSize: 12, color: "#666" }}>Usuários por página</span>
          <select
            value={pageSize}
            onChange={(e) => {
              const n = Number(e.target.value);
              setPageSize(n);
              setPage(1);
            }}
            style={{ height: 30, border: "1px solid #E3E3E3", borderRadius: 6, padding: "0 8px", outline: "none" }}
            disabled={loading}
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </select>

          <span style={{ fontSize: 12, color: "#666" }}>{total === 0 ? "0-0 de 0" : `${startIndex + 1}-${endIndex} de ${total}`}</span>

          <button type="button" className="btn btn-link" onClick={goFirst} disabled={!canPrev || loading} style={{ padding: 6 }} title="Primeira página">
            <FiChevronsLeft size={18} opacity={!canPrev || loading ? 0.35 : 1} />
          </button>
          <button type="button" className="btn btn-link" onClick={goPrev} disabled={!canPrev || loading} style={{ padding: 6 }} title="Página anterior">
            <FiChevronLeft size={18} opacity={!canPrev || loading ? 0.35 : 1} />
          </button>
          <button type="button" className="btn btn-link" onClick={goNext} disabled={!canNext || loading} style={{ padding: 6 }} title="Próxima página">
            <FiChevronRight size={18} opacity={!canNext || loading ? 0.35 : 1} />
          </button>
          <button type="button" className="btn btn-link" onClick={goLast} disabled={!canNext || loading} style={{ padding: 6 }} title="Última página">
            <FiChevronsRight size={18} opacity={!canNext || loading ? 0.35 : 1} />
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default SelectUserModal;
